import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useSupportSession } from '@/hooks/useSupportSession'
import { Receipt, Plus, Search, Download } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Button,
  Card,
  Badge,
  Checkbox,
  EmptyState,
  SetupGuide,
  Select,
  Pagination,
  DataTable,
  Skeleton,
  Input,
  CardKebabMenu,
  ConfirmDialog,
} from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useExpenses, useDeleteExpense } from '@/hooks/useExpenses'
import { useProperties } from '@/hooks/useProperties'
import { useListSort } from '@/hooks/useListSort'
import { useDeepLinkParams } from '@/hooks/useDeepLinkParams'
import { useSelectedPropertyStore, useNotificationStore } from '@/stores'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { api } from '@/lib/api'
import type { Expense } from '@/types'
import { formatBaht } from '@/lib/money'

export function ExpenseListPage() {
  const { user } = useAuth()
  // EP-142 (owner ruling: RESTful format negotiation) — the CSV representation of
  // GET /api/expenses is admin_or_higher? || accountant?; staff gets 403, so hide the affordance
  const canExportCsv = user?.role !== 'staff'
  const supportSession = useSupportSession().active

  const { t, i18n } = useTranslation('expenses')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const translateError = useApiErrorTranslator('expenses')
  const addToast = useNotificationStore((s) => s.addToast)

  const [page, setPage] = useState(1)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const [pendingDelete, setPendingDelete] = useState<Expense | null>(null)
  const deleteExpense = useDeleteExpense()
  // EP-155 / EP-129: the dashboard and the closing package link here with
  // ?missing_receipt=true (and the month's from/to); those land as filters.
  // EP-156 (T156-019): the owner/admin to-do card links here with
  // ?status=pending_confirmation.
  const deepLink = useDeepLinkParams(['missing_receipt', 'has_wht', 'from', 'to', 'status'] as const)
  const [category, setCategory] = useState('')
  const [status, setStatus] = useState(deepLink.status ?? '')
  const [from, setFrom] = useState(deepLink.from ?? '')
  const [to, setTo] = useState(deepLink.to ?? '')
  const [searchQuery, setSearchQuery] = useState('')
  const [missingReceipt, setMissingReceipt] = useState(deepLink.missing_receipt === 'true')
  const [hasWht, setHasWht] = useState(deepLink.has_wht === 'true')
  const [isExporting, setIsExporting] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, isFetching, error } = useExpenses({
    page,
    property_id: selectedPropertyId ?? undefined,
    category: category || undefined,
    status: status || undefined,
    from: from || undefined,
    to: to || undefined,
    q: searchQuery || undefined,
    missing_receipt: missingReceipt || undefined,
    has_wht: hasWht || undefined,
    ...sortParams,
  })

  // Unfiltered query to decide setup guide vs empty state
  const { data: totalData } = useExpenses({ page: 1 })
  const totalCount = totalData?.meta?.total_count ?? 0

  const { data: propertiesData } = useProperties()
  const properties = propertiesData?.data ?? []
  const hasProperties = properties.length > 0

  const expenses = data?.data ?? []

  const propertyOptions = [
    { value: '', label: tc('labels.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const categoryOptions = [
    { value: '', label: t('filters.all_categories') },
    { value: 'repairs', label: t('categories.repairs') },
    { value: 'utilities', label: t('categories.utilities') },
    { value: 'insurance', label: t('categories.insurance') },
    { value: 'property_tax', label: t('categories.property_tax') },
    { value: 'labor', label: t('categories.labor') },
    { value: 'supplies', label: t('categories.supplies') },
    { value: 'cleaning', label: t('categories.cleaning') },
    { value: 'security', label: t('categories.security') },
    { value: 'management_fee', label: t('categories.management_fee') },
    { value: 'office_equipment', label: t('categories.office_equipment') },
    { value: 'other', label: t('categories.other') },
  ]

  const statusOptions = [
    { value: '', label: t('filters.all_statuses') },
    // T156-019 (EP-156 §5 decision 15): every new non-reimbursement expense
    // starts here and waits for owner/admin confirmation.
    { value: 'pending_confirmation', label: t('statuses.pending_confirmation') },
    { value: 'completed', label: t('statuses.completed') },
    { value: 'pending_reimbursement_approval', label: t('statuses.pending_reimbursement_approval') },
    { value: 'reimbursement_approved', label: t('statuses.reimbursement_approved') },
  ]

  const getStatusBadge = (expense: Expense) => {
    switch (expense.status) {
      case 'completed':
        return <Badge variant="success">{t('statuses.completed')}</Badge>
      case 'pending_reimbursement_approval':
        return <Badge variant="warning">{t('statuses.pending_reimbursement_approval')}</Badge>
      case 'reimbursement_approved':
        return <Badge variant="info">{t('statuses.reimbursement_approved')}</Badge>
      case 'pending_confirmation':
        return <Badge variant="warning">{t('statuses.pending_confirmation')}</Badge>
      default:
        return <Badge variant="ghost">{expense.status}</Badge>
    }
  }

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      const params: Record<string, unknown> = {}
      if (selectedPropertyId) params.property_id = selectedPropertyId
      if (category) params.category = category
      if (status) params.status = status
      if (from) params.from = from
      if (to) params.to = to
      if (searchQuery) params.q = searchQuery
      if (missingReceipt) params.missing_receipt = 'true'
      if (hasWht) params.has_wht = 'true'

      const response = await api.get('/api/expenses.csv', {
        params,
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'expenses.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setIsExporting(false)
    }
  }

  const columns: Column<Expense>[] = [
    {
      key: 'actions',
      header: '',
      className: 'w-10',
      render: (e) => (
        <div onClick={(ev) => ev.stopPropagation()}>
          <CardKebabMenu
            ariaLabel={tc('labels.actions_menu')}
            items={[
              { label: tc('actions.view'), onClick: () => navigate(`/expenses/${e.id}`) },
              { label: tc('actions.edit'), onClick: () => navigate(`/expenses/${e.id}/edit`) },
              { label: tc('actions.delete'), variant: 'error', onClick: () => setPendingDelete(e) },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('labels.amount'),
      sortable: true,
      render: (e) => <span className="font-medium whitespace-nowrap">{formatBaht(e.amount, { locale: 'en', decimals: 2 })}</span>,
    },
    {
      key: 'category',
      header: t('labels.category'),
      sortable: true,
      render: (e) => (
        // expense.category is operator-configurable (accountSettings.settings.expense_categories),
        // not a fixed backend enum — branch on i18n.exists() so custom categories render as-is.
        <span>
          {i18n.exists(`expenses:categories.${e.category}`) ? t(`categories.${e.category}`) : e.category}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('labels.description'),
      render: (e) => <span className="block max-w-[10rem] truncate" title={e.description?.trim() || undefined}>{e.description?.trim() || '—'}</span>,
    },
    {
      key: 'status',
      header: t('labels.status'),
      render: (e) => getStatusBadge(e),
    },
    {
      key: 'expense_date',
      header: t('labels.date'),
      sortable: true,
      render: (e) => <span className="whitespace-nowrap">{formatDate(e.expense_date)}</span>,
    },
    {
      key: 'vendor_name',
      header: t('labels.vendor_name'),
      render: (e) => <span>{e.vendor_name ?? '-'}</span>,
      hideBelow: 'md',
    },
    {
      key: 'property_name',
      header: t('labels.property'),
      render: (e) => <span>{e.property_name}</span>,
      hideBelow: 'md',
    },
    {
      key: 'vat_amount',
      header: t('labels.vat_amount'),
      render: (e) => <span>{formatBaht(e.vat_amount, { locale: 'en', decimals: 2 })}</span>,
      hideBelow: 'lg',
    },
    {
      key: 'paid_by',
      header: t('labels.paid_by'),
      render: (e) => <span>{e.paid_by ?? '-'}</span>,
      hideBelow: 'md',
    },
    {
      key: 'created_by_name',
      header: t('labels.created_by'),
      render: (e) => <span>{e.created_by_name}</span>,
      hideBelow: 'lg',
    },
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns. Labels
  // reuse the column-header translations; the default updated_at gets a shared key.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'expense_date', label: t('labels.date') },
    { value: 'amount', label: t('labels.amount') },
    { value: 'category', label: t('labels.category') },
  ]

  const activeFilterCount = [category, status, from, to, missingReceipt || null, hasWht || null].filter(Boolean).length

  return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          isRefreshing={isFetching && !isLoading}
          titleAction={
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tc('labels.select_property')}
              value={selectedPropertyId ? String(selectedPropertyId) : ''}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
                setPage(1)
              }}
              options={propertyOptions}
              className="max-w-[9rem]"
              size="sm"
            />
          }
          primaryAction={{
            key: 'new-expense',
            label: t('actions.new_expense'),
            icon: Plus,
            to: '/expenses/new',
          }}
          overflowActions={[
            {
              key: 'export',
              label: t('actions.export_csv'),
              icon: Download,
              onClick: handleExportCsv,
              loading: isExporting,
              // EP-160 S3: the CSV is refused in a FlowRent support session.
              hidden: !canExportCsv || supportSession,
            },
          ]}
        />

        {/* Filters */}
        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              id="expense-search"
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('placeholders.search')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
            />
          </div>

          {/* Mobile-only toggle */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="expense-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="expense-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            <Select
              id="expense-category-filter"
              label={t('labels.category')}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value)
                setPage(1)
              }}
              options={categoryOptions}
              className="w-full sm:w-52 min-w-0"
            />

            <Select
              id="expense-status-filter"
              label={t('labels.status')}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              options={statusOptions}
              className="w-full sm:w-48 min-w-0"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full sm:flex-1 sm:min-w-[280px]">
              <Input
                id="expense-date-from"
                label={t('filters.date_from')}
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value)
                  setPage(1)
                }}
              />
              <Input
                id="expense-date-to"
                label={t('filters.date_to')}
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value)
                  setPage(1)
                }}
              />
            </div>

            {/* T129-011 D-8 completeness filters */}
            <div className="flex flex-wrap gap-4 items-center w-full">
              <Checkbox
                label={t('filters.missing_receipt')}
                checked={missingReceipt}
                onChange={(e) => {
                  setMissingReceipt(e.target.checked)
                  setPage(1)
                }}
                data-testid="filter-missing-receipt"
              />
              <Checkbox
                label={t('filters.has_wht')}
                checked={hasWht}
                onChange={(e) => {
                  setHasWht(e.target.checked)
                  setPage(1)
                }}
                data-testid="filter-has-wht"
              />
            </div>
          </div>
        </Card>

        {/* Content */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              description={tc('errors.not_found')}
              action={
                <Button onClick={() => window.location.reload()}>
                  {tc('actions.retry')}
                </Button>
              }
            />
          </Card>
        ) : !expenses.length ? (
          <Card>
            {totalCount === 0 ? (
              <SetupGuide
                icon={<Receipt className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.prereq_property'),
                    met: hasProperties,
                    link: '/properties',
                    linkLabel: t('setup_guide.prereq_setup_property'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                  { title: t('setup_guide.step_3_title'), description: t('setup_guide.step_3_description') },
                ]}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate('/expenses/new')}
                    disabled={!hasProperties}
                  >
                    {t('actions.new_expense')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Receipt className="w-12 h-12" />}
                title={t('empty.title')}
                description={t('empty.description')}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate('/expenses/new')}
                  >
                    {t('actions.new_expense')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={expenses}
              keyExtractor={(e) => e.id}
              onRowClick={(e) => navigate(`/expenses/${e.id}`)}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
            />
            {data?.meta && data.meta.total_count > 0 && (
              <div className="flex justify-center mt-4">
                <Pagination
                  currentPage={data.meta.current_page}
                  totalPages={data.meta.total_pages}
                  totalItems={data.meta.total_count}
                  pageSize={data.meta.per_page}
                  onPageChange={setPage}
                />
              </div>
            )}
          </Card>
        )}

        {!isLoading && !error && data?.meta && (expenses.length > 0 || totalCount > 0) && (
          <Card compact>
            <dl className="flex flex-wrap items-center justify-between gap-2">
              <dt className="font-medium">{t(`totals.${data.meta.total_basis}`)}</dt>
              <dd className="font-semibold tabular-nums whitespace-nowrap">
                {formatBaht(data.meta.total_amount, { locale: 'en', decimals: 2 })}
              </dd>
            </dl>
          </Card>
        )}

        <ConfirmDialog
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            if (!pendingDelete) return
            await deleteExpense.mutateAsync(pendingDelete.id)
            setPendingDelete(null)
          }}
          title={tc('actions.delete')}
          message={t('messages.delete_confirm')}
          confirmLabel={tc('actions.delete')}
          cancelLabel={tc('actions.cancel')}
          variant="error"
          loading={deleteExpense.isPending}
        />
      </div>
  )
}
